import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ChevronLeft, Loader2, RefreshCw, Download, Share2, AlertTriangle, Anchor, Users, Clock, Edit2, X, Plus, BookOpenCheck, Thermometer, Gauge, Hourglass, MapPin, ArrowLeftRight, ClipboardList, FileText, Mail, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { plannerApi } from './plannerApi';
import PwaInstallBanner from '../../components/PwaInstallBanner';
import ImportPlanDialog from './ImportPlanDialog';

const ROLE_COLOR = {
  instructor: 'bg-orange-500',
  divemaster: 'bg-blue-500',
};

const subKitUp = (t) => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return '';
  const [h, m] = t.split(':').map(Number);
  const total = h * 60 + m - 60;
  if (total < 0) return '';
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const DayPlanner = () => {
  const { tripId, planDate } = useParams();
  const { token } = useAuth();
  const api = plannerApi(token);
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [diverMenu, setDiverMenu] = useState(null); // { diverId, fromLaunch }
  const [crewMenu, setCrewMenu] = useState(null); // { crewId, fromLaunch } or { addToLaunch: idx }
  const [editTimes, setEditTimes] = useState(false);
  const [draftTimes, setDraftTimes] = useState([]);
  const [logModal, setLogModal] = useState(null); // launch index being edited
  const [logForm, setLogForm] = useState({ date: '', time: '', site: '', depth_m: '', water_temp_c: '', duration_min: '', recorded_by_name: '', notes: '' });
  const [savingLog, setSavingLog] = useState(false);
  const [swapModal, setSwapModal] = useState(null); // launch index initiating the swap
  const [rollCallOpen, setRollCallOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const t = await api.getTrip(tripId);
      setTrip(t);
      const p = (t.daily_plans || []).find((dp) => dp.date === planDate);
      setPlan(p || null);
    } catch (e) { toast.error('Failed to load plan'); }
  }, [tripId, planDate]); // eslint-disable-line

  useEffect(() => {
    let active = true;
    (async () => { await load(); if (active) setLoading(false); })();
    return () => { active = false; };
  }, [load]);

  const persist = async (launches, launchTimes) => {
    setSaving(true);
    try {
      const payload = {
        launches: launches.map((L) => ({
          launch_index: L.index,
          diver_ids: L.divers,
          crew_ids: L.crew,
          casual_crew_ids: L.casual_crew_ids || [],
        })),
      };
      if (launchTimes !== undefined) payload.launch_times = launchTimes;
      const updated = await api.manualUpdatePlan(tripId, planDate, payload);
      setPlan(updated);
    } catch (e) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const toggleCrewMode = async (launchIndex, crewId) => {
    // Per-launch Working/Casual toggle for crew members. Working (default) =
    // non-billable; Casual = billed at the casual rate set on the trip.
    const newLaunches = plan.launches.map((L) => {
      if (L.index !== launchIndex) return L;
      const existing = new Set(L.casual_crew_ids || []);
      if (existing.has(crewId)) existing.delete(crewId); else existing.add(crewId);
      return { ...L, casual_crew_ids: Array.from(existing) };
    });
    setPlan({ ...plan, launches: newLaunches });
    await persist(newLaunches);
  };

  const removeDiverFromLaunch = async () => {
    if (!diverMenu) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    newLaunches[diverMenu.fromLaunch].divers = newLaunches[diverMenu.fromLaunch].divers.filter((id) => id !== diverMenu.diverId);
    setPlan({ ...plan, launches: newLaunches });
    setDiverMenu(null);
    await persist(newLaunches);
    toast.success('Removed from launch');
  };

  const removeDiverFromDay = async () => {
    if (!diverMenu) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: L.divers.filter((id) => id !== diverMenu.diverId), crew: [...L.crew] }));
    setPlan({ ...plan, launches: newLaunches });
    setDiverMenu(null);
    await persist(newLaunches);
    toast.success('Removed from day');
  };

  const moveDiverToLaunch = async (targetIdx) => {
    if (!diverMenu) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    if (newLaunches[targetIdx].divers.includes(diverMenu.diverId)) {
      toast.error('Already on that launch'); setDiverMenu(null); return;
    }
    newLaunches[diverMenu.fromLaunch].divers = newLaunches[diverMenu.fromLaunch].divers.filter((id) => id !== diverMenu.diverId);
    newLaunches[targetIdx].divers.push(diverMenu.diverId);
    setPlan({ ...plan, launches: newLaunches });
    setDiverMenu(null);
    await persist(newLaunches);
    toast.success('Diver moved');
  };

  // Swap the selected diver with another diver on a target launch.
  const swapDiverWith = async (targetLaunchIdx, otherDiverId) => {
    if (!diverMenu) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    const meIdx = newLaunches[diverMenu.fromLaunch].divers.indexOf(diverMenu.diverId);
    const otherIdx = newLaunches[targetLaunchIdx].divers.indexOf(otherDiverId);
    if (meIdx === -1 || otherIdx === -1) { toast.error('Could not find both divers'); return; }
    newLaunches[diverMenu.fromLaunch].divers[meIdx] = otherDiverId;
    newLaunches[targetLaunchIdx].divers[otherIdx] = diverMenu.diverId;
    setPlan({ ...plan, launches: newLaunches });
    setDiverMenu(null);
    await persist(newLaunches);
    toast.success('Divers swapped');
  };

  // Add a diver who isn't currently on this launch (from the trip roster).
  const addDiverToLaunch = async (diverId) => {
    if (!diverMenu || diverMenu.addToLaunch == null) return;
    const targetIdx = diverMenu.addToLaunch;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    if (newLaunches[targetIdx].divers.includes(diverId)) {
      toast.error('Already on this launch'); return;
    }
    newLaunches[targetIdx].divers.push(diverId);
    setPlan({ ...plan, launches: newLaunches });
    setDiverMenu(null);
    await persist(newLaunches);
    toast.success('Diver added');
  };

  const removeCrewFromLaunch = async () => {
    if (!crewMenu || crewMenu.fromLaunch == null) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    newLaunches[crewMenu.fromLaunch].crew = newLaunches[crewMenu.fromLaunch].crew.filter((id) => id !== crewMenu.crewId);
    setPlan({ ...plan, launches: newLaunches });
    setCrewMenu(null);
    await persist(newLaunches);
    toast.success('Crew removed from launch');
  };

  const moveCrewToLaunch = async (targetIdx) => {
    if (!crewMenu || crewMenu.fromLaunch == null) return;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    if (newLaunches[targetIdx].crew.includes(crewMenu.crewId)) {
      toast.error('Already on that launch'); setCrewMenu(null); return;
    }
    newLaunches[crewMenu.fromLaunch].crew = newLaunches[crewMenu.fromLaunch].crew.filter((id) => id !== crewMenu.crewId);
    newLaunches[targetIdx].crew.push(crewMenu.crewId);
    setPlan({ ...plan, launches: newLaunches });
    setCrewMenu(null);
    await persist(newLaunches);
    toast.success('Crew moved');
  };

  const addCrewToLaunch = async (crewId) => {
    if (!crewMenu || crewMenu.addToLaunch == null) return;
    const targetIdx = crewMenu.addToLaunch;
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    if (newLaunches[targetIdx].crew.includes(crewId)) {
      toast.error('Already on this launch'); return;
    }
    // Crew can now appear on multiple launches — e.g. an instructor who
    // does back-to-back launches. We no longer auto-remove from other
    // launches; use Move / Remove explicitly if you want that.
    newLaunches[targetIdx].crew.push(crewId);
    setPlan({ ...plan, launches: newLaunches });
    setCrewMenu(null);
    await persist(newLaunches);
    toast.success('Crew added');
  };

  const openLogModal = (launchIdx) => {
    const L = plan.launches.find((x) => x.index === launchIdx);
    const existing = L?.log || {};
    setLogForm({
      date: existing.date || planDate,
      time: existing.time || (plan.launch_times?.[launchIdx] || ''),
      site: existing.site || '',
      depth_m: existing.depth_m ?? '',
      water_temp_c: existing.water_temp_c ?? '',
      duration_min: existing.duration_min ?? '',
      recorded_by_name: existing.recorded_by_name || '',
      notes: existing.notes || '',
    });
    setLogModal(launchIdx);
  };

  const saveLog = async () => {
    if (logModal == null) return;
    setSavingLog(true);
    try {
      const payload = {
        date: logForm.date || planDate,
        time: logForm.time || null,
        site: logForm.site || null,
        depth_m: logForm.depth_m === '' ? null : Number(logForm.depth_m),
        water_temp_c: logForm.water_temp_c === '' ? null : Number(logForm.water_temp_c),
        duration_min: logForm.duration_min === '' ? null : Number(logForm.duration_min),
        recorded_by_name: logForm.recorded_by_name || null,
        notes: logForm.notes || null,
      };
      const updated = await api.updateLaunchLog(tripId, planDate, logModal, payload);
      setPlan(updated);
      setLogModal(null);
      toast.success('Dive log saved — divers can see it immediately');
    } catch (e) {
      toast.error('Failed to save log');
    } finally {
      setSavingLog(false);
    }
  };

  const clearLog = async () => {
    if (logModal == null) return;
    if (!window.confirm('Remove this dive log?')) return;
    setSavingLog(true);
    try {
      const updated = await api.updateLaunchLog(tripId, planDate, logModal, null);
      setPlan(updated);
      setLogModal(null);
      toast.success('Dive log removed');
    } catch (e) {
      toast.error('Failed to remove log');
    } finally {
      setSavingLog(false);
    }
  };

  const saveTimes = async () => {
    await persist(plan.launches, draftTimes);
    setEditTimes(false);
    toast.success('Times updated');
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    // draggableId format: "{kind}-{launchIndex}-{personId}" — launchIndex makes
    // each row unique even when the same diver/crew appears on multiple launches.
    const match = draggableId.match(/^(diver|crew)-(\d+)-(.+)$/);
    if (!match) return;
    const kind = match[1];
    const id = match[3];
    const list = kind === 'diver' ? 'divers' : 'crew';
    const fromIdx = Number(source.droppableId.split('-').pop());
    const toIdx = Number(destination.droppableId.split('-').pop());
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    // Work on a deep copy of launches so we never mutate React state directly.
    const newLaunches = plan.launches.map((L) => ({ ...L, divers: [...L.divers], crew: [...L.crew] }));
    // Look up launches by their `index` field rather than array position so
    // we stay correct even if launches are ever reordered in state.
    const fromArr = newLaunches.findIndex((L) => L.index === fromIdx);
    const toArr = newLaunches.findIndex((L) => L.index === toIdx);
    if (fromArr === -1 || toArr === -1) return;
    // Prevent the same diver landing on the same launch twice.
    if (kind === 'diver' && newLaunches[toArr].divers.includes(id) && fromIdx !== toIdx) {
      toast.error('Diver already on this launch');
      return;
    }
    newLaunches[fromArr][list].splice(source.index, 1);
    newLaunches[toArr][list].splice(destination.index, 0, id);
    setPlan({ ...plan, launches: newLaunches });
    await persist(newLaunches);
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate this plan? Manual changes will be lost.')) return;
    setRegenerating(true);
    try {
      const boats = (plan.boats && plan.boats.length > 0)
        ? plan.boats.map((b) => ({ name: b.name, launches_count: b.launches_count, capacity_per_launch: b.capacity_per_launch }))
        : null;
      const payload = {
        date: planDate,
        auto_generate: true,
        launch_times: plan.launch_times,
      };
      if (boats) {
        payload.boats = boats;
      } else {
        payload.launches = plan.launches_count;
        payload.capacity_per_launch = plan.capacity_per_launch;
      }
      const updated = await api.generatePlan(tripId, payload);
      setPlan(updated);
      toast.success('Regenerated');
    } catch (e) { toast.error('Failed to regenerate'); } finally { setRegenerating(false); }
  };

  const swapWith = async (targetIdx) => {
    if (swapModal === null || targetIdx === swapModal) return;
    try {
      const updated = await api.swapLaunches(tripId, planDate, swapModal, targetIdx);
      setPlan(updated);
      setSwapModal(null);
      toast.success(`Launch ${swapModal + 1} ↔ Launch ${targetIdx + 1} swapped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to swap');
    }
  };

  const setDiverAttendance = async (diverId, status) => {
    try {
      const updated = await api.setAttendance(tripId, planDate, diverId, status);
      setPlan(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update attendance');
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('download fail');
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error('Download failed'); }
  };

  const downloadDiveReport = (kind) => {
    const url = kind === 'pdf' ? api.diveReportPdfUrl(tripId, planDate) : api.diveReportCsvUrl(tripId, planDate);
    const filename = `DiveReport_${(trip.name || 'trip').replace(/\s+/g, '_')}_${planDate}.${kind}`;
    return downloadFile(url, filename);
  };

  const openEmailModal = () => {
    const saved = trip?.report_recipients || [];
    setEmailRecipients(saved.join(', '));
    setEmailMessage('');
    setEmailModal(true);
  };

  const sendDiveReportEmail = async () => {
    const recipients = emailRecipients
      .split(/[,;\n]/)
      .map((e) => e.trim())
      .filter((e) => e && e.includes('@'));
    if (recipients.length === 0) {
      toast.error('Enter at least one email');
      return;
    }
    setEmailSending(true);
    try {
      const res = await api.emailDiveReport(tripId, planDate, recipients, emailMessage.trim() || null);
      const okCount = (res.sent_to || []).length;
      const failCount = (res.failed || []).length;
      if (okCount > 0) toast.success(`Report sent to ${okCount} recipient${okCount > 1 ? 's' : ''}`);
      if (failCount > 0) toast.error(`Failed for ${failCount}: ${(res.failed || []).join(', ')}`);
      if (okCount > 0) setEmailModal(false);
      // Update local trip cache with the saved recipients.
      if (res.saved_recipients) {
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setEmailSending(false);
    }
  };

  const downloadPdf = async () => {
    return downloadFile(api.exportPdfUrl(tripId, planDate), `DivePlan_${trip.name}_${planDate}.pdf`);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(api.shareLink(plan.share_token));
      toast.success('Share link copied');
    } catch (e) { toast.error('Failed to copy'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" /></div>;
  if (!trip || !plan) return <div className="p-8 text-center"><Link to={`/planner/trips/${tripId}`} className="text-[#FF6B35]">← Back to trip</Link><p className="mt-4 text-gray-500">Plan not found.</p></div>;

  const diverMap = Object.fromEntries((trip.divers || []).map((d) => [d.id, d]));
  const crewMap = Object.fromEntries((trip.crew || []).map((c) => [c.id, c]));

  // Group launches by boat for rendering.
  const boats = plan.boats && plan.boats.length > 0
    ? plan.boats
    : [{ index: 0, name: 'Boat A', launches_count: plan.launches_count, capacity_per_launch: plan.capacity_per_launch }];
  const boatGroups = boats.map((b, bi) => ({
    boat: b,
    boat_index: b.index ?? bi,
    launches: plan.launches.filter((L) => (L.boat_index ?? 0) === (b.index ?? bi)),
  }));

  const renderLaunchCard = (L) => {
    const boatCapacity = boats.find((b) => (b.index ?? 0) === (L.boat_index ?? 0))?.capacity_per_launch || plan.capacity_per_launch;
    const launchLabel = L.launch_in_boat != null ? L.launch_in_boat + 1 : L.index + 1;
    return (
      <div key={L.index} className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid={`launch-${L.index}`}>
        <div className="bg-[#0A2342] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Anchor className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="font-semibold">Launch {launchLabel}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSwapModal(L.index)}
              className="text-white/80 hover:text-white p-1"
              title="Swap with another launch"
              data-testid={`launch-swap-${L.index}`}
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <span className="text-xs opacity-80">{L.divers.length + L.crew.length}/{boatCapacity}</span>
          </div>
        </div>
        {(plan.launch_times?.[L.index]) && (
          <div className="bg-slate-50 px-4 py-2 text-xs flex items-center gap-3 border-b">
            <Clock className="w-3.5 h-3.5 text-[#FF6B35]" />
            <span><strong>Launch:</strong> {plan.launch_times[L.index]}</span>
            <span className="text-gray-500"><strong>Kit-up:</strong> {subKitUp(plan.launch_times[L.index]) || '—'}</span>
          </div>
        )}

        <Droppable droppableId={`crew-${L.index}`} type="crew">
          {(provided, snap) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={`px-3 py-2 border-b min-h-[60px] ${snap.isDraggingOver ? 'bg-orange-50' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" /> CREW</div>
                <button
                  onClick={() => setCrewMenu({ addToLaunch: L.index })}
                  className="text-xs text-[#FF6B35] hover:text-[#0A2342] flex items-center gap-0.5 font-medium"
                  data-testid={`crew-add-${L.index}`}
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {L.crew.length === 0 && <div className="text-xs text-red-500">⚠ No crew assigned</div>}
              {L.crew.map((cid, idx) => {
                const c = crewMap[cid]; if (!c) return null;
                const isCasual = (L.casual_crew_ids || []).includes(cid);
                return (
                  <Draggable key={`crew-${L.index}-${cid}`} draggableId={`crew-${L.index}-${cid}`} index={idx}>
                    {(p) => (
                      <div ref={p.innerRef} {...p.draggableProps} className={`flex items-center justify-between gap-2 py-1 px-2 my-1 rounded-lg text-sm ${isCasual ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                        <div {...p.dragHandleProps} className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`w-2 h-2 rounded-full ${ROLE_COLOR[c.role] || 'bg-gray-400'}`}></span>
                          <span className="font-medium text-[#0A2342] truncate">{c.name}</span>
                          <span className="text-xs text-gray-400 capitalize">{c.role}</span>
                        </div>
                        <button
                          onClick={() => toggleCrewMode(L.index, cid)}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold tracking-wide ${isCasual ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-700 hover:bg-amber-100 hover:text-amber-800'}`}
                          title={isCasual ? 'Billed as casual recreational dive — click to mark working' : 'Non-billable working dive — click to mark casual (billable)'}
                          data-testid={`crew-mode-toggle-${cid}-${L.index}`}
                        >
                          {isCasual ? 'CASUAL' : 'WORKING'}
                        </button>
                        <button
                          onClick={() => setCrewMenu({ crewId: cid, fromLaunch: L.index })}
                          className="text-gray-300 hover:text-[#0A2342] p-1"
                          aria-label="Crew options"
                          data-testid={`crew-options-${cid}-${L.index}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        <Droppable droppableId={`diver-${L.index}`} type="diver">
          {(provided, snap) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={`px-3 py-2 min-h-[120px] ${snap.isDraggingOver ? 'bg-blue-50' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-gray-500">DIVERS</div>
                <button
                  onClick={() => setDiverMenu({ addToLaunch: L.index })}
                  className="text-xs text-[#FF6B35] hover:text-[#0A2342] flex items-center gap-0.5 font-medium"
                  data-testid={`diver-add-${L.index}`}
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {L.divers.map((did, idx) => {
                // Some "divers" are actually buddied crew members (e.g.
                // Andre paired with Sally). Fall back to the crew roster
                // for their display info.
                const d = diverMap[did] || crewMap[did];
                if (!d) return null;
                const isBuddiedCrew = !diverMap[did] && !!crewMap[did];
                const attendance = plan.attendance?.[did]; // 'diving' | 'not_diving' | 'undecided'
                return (
                  <Draggable key={`diver-${L.index}-${did}`} draggableId={`diver-${L.index}-${did}`} index={idx}>
                    {(p) => (
                      <div ref={p.innerRef} {...p.draggableProps} className={`flex items-center justify-between gap-2 py-1 px-2 my-1 ${isBuddiedCrew ? 'bg-orange-50 border-orange-200' : 'bg-white'} ${attendance === 'not_diving' ? 'opacity-50 line-through' : ''} border rounded-lg text-sm hover:border-[#FF6B35]`}>
                        <div {...p.dragHandleProps} className="flex items-center gap-2 min-w-0 flex-1" title={!isBuddiedCrew && (d.certification_level || d.prior_dives != null) ? `${d.certification_level || 'Unknown'}${d.prior_dives != null ? ` · ${d.prior_dives} prior dives` : ''}` : undefined}>
                          <span className="text-xs text-gray-400 w-4">{idx + 1}.</span>
                          <span className={`font-medium truncate ${isBuddiedCrew ? 'text-[#FF6B35]' : 'text-[#0A2342]'}`}>{d.name}</span>
                          {isBuddiedCrew && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded capitalize">{d.role || 'staff'} · buddy</span>}
                          {!isBuddiedCrew && d.certification_level === 'Student' && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Student</span>}
                          {!isBuddiedCrew && d.nitrox && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">N</span>}
                          {attendance === 'not_diving' && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">not diving</span>}
                          {attendance === 'undecided' && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">undecided</span>}
                        </div>
                        <button
                          onClick={() => setDiverMenu({ diverId: did, fromLaunch: L.index, isBuddiedCrew })}
                          className="text-gray-300 hover:text-[#0A2342] p-1"
                          aria-label="Diver options"
                          data-testid={`diver-options-${did}-${L.index}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {/* Instructor's dive log */}
        <div className="border-t bg-slate-50 px-4 py-2">
          {L.log ? (
            <div className="space-y-1" data-testid={`launch-log-${L.index}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-[#0A2342] flex items-center gap-1">
                  <BookOpenCheck className="w-3.5 h-3.5 text-[#FF6B35]" /> Instructor&apos;s Dive Log
                </div>
                <button onClick={() => openLogModal(L.index)} className="text-xs text-[#FF6B35] hover:underline" data-testid={`edit-log-${L.index}`}>Edit</button>
              </div>
              <p className="text-[10px] text-gray-500 italic leading-snug">
                NOTE: The following information is from <strong>{L.log.recorded_by_name || 'Crew'}</strong> and is here for reference only.
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#0A2342]">
                {L.log.date && <span><Clock className="w-3 h-3 inline" /> {L.log.date}{L.log.time ? ` ${L.log.time}` : ''}</span>}
                {L.log.site && <span><MapPin className="w-3 h-3 inline" /> {L.log.site}</span>}
                {L.log.depth_m != null && <span><Gauge className="w-3 h-3 inline" /> {L.log.depth_m} m</span>}
                {L.log.water_temp_c != null && <span><Thermometer className="w-3 h-3 inline" /> {L.log.water_temp_c}°C</span>}
                {L.log.duration_min != null && <span><Hourglass className="w-3 h-3 inline" /> {L.log.duration_min} min</span>}
              </div>
              {L.log.notes && <p className="text-[11px] text-gray-600">{L.log.notes}</p>}
            </div>
          ) : (
            <button
              onClick={() => openLogModal(L.index)}
              className="text-xs text-[#0A2342] hover:text-[#FF6B35] flex items-center gap-1 font-medium"
              data-testid={`add-log-${L.index}`}
            >
              <BookOpenCheck className="w-3.5 h-3.5" /> Add instructor&apos;s dive log
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-5 pb-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto">
          <Link to={`/planner/trips/${tripId}`} className="text-sm opacity-80 flex items-center gap-1 mb-1"><ChevronLeft className="w-4 h-4" /> Back</Link>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-lg md:text-xl font-bold">{trip.name}</h1>
              <p className="text-xs opacity-80">
                {planDate} · {boats.map((b, i) => `${b.name || `Boat ${String.fromCharCode(65 + i)}`} (${b.launches_count}×${b.capacity_per_launch})`).join(' · ')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Import offline plan (WhatsApp paste or screenshot)" onClick={() => setImportOpen(true)} data-testid="day-import-btn"><Upload className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Roll call" onClick={() => setRollCallOpen(true)} data-testid="day-rollcall-btn"><ClipboardList className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Edit launch times" onClick={() => { setDraftTimes(plan.launch_times || Array(plan.launches_count).fill('')); setEditTimes(true); }} data-testid="day-edit-times-btn"><Clock className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Share link" onClick={copyShareLink} data-testid="day-share-btn"><Share2 className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Launch schedule PDF" onClick={downloadPdf} data-testid="day-pdf-btn"><Download className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Dive report PDF" onClick={() => downloadDiveReport('pdf')} data-testid="day-report-pdf-btn"><FileText className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 text-[10px] font-semibold" title="Dive report CSV" onClick={() => downloadDiveReport('csv')} data-testid="day-report-csv-btn">CSV</Button>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" title="Email dive report" onClick={openEmailModal} data-testid="day-email-report-btn"><Mail className="w-4 h-4" /></Button>
              <Button size="sm" className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" onClick={regenerate} disabled={regenerating} data-testid="day-regenerate-btn">
                {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-3">
        {plan.forecast && (() => {
          const f = plan.forecast;
          const sev = f.severity || 'calm';
          const palette = sev === 'rough'
            ? { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', tag: 'bg-red-600' }
            : sev === 'lumpy'
              ? { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', tag: 'bg-amber-500' }
              : { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', tag: 'bg-emerald-600' };
          const fmt = (v, d = 1) => (typeof v === 'number' ? v.toFixed(d) : '—');
          return (
            <div className={`rounded-xl border p-3 mb-3 ${palette.bg} ${palette.border}`} data-testid="weather-forecast-card">
              <div className="flex items-center gap-2 mb-2">
                <Anchor className={`w-4 h-4 ${palette.text}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${palette.text}`}>Sea state forecast</span>
                <span className={`text-[10px] text-white px-2 py-0.5 rounded-full uppercase font-bold ${palette.tag}`} data-testid="weather-severity">
                  {sev}
                </span>
                <span className="text-[10px] text-gray-500 ml-auto">via open-meteo.com</span>
              </div>
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-2 text-xs ${palette.text}`}>
                <div>
                  <div className="opacity-70 text-[10px] uppercase">Waves</div>
                  <div className="font-bold text-base" data-testid="weather-wave">{fmt(f.wave_height_m)} m</div>
                </div>
                <div>
                  <div className="opacity-70 text-[10px] uppercase">Swell</div>
                  <div className="font-bold text-base">{fmt(f.swell_height_m)} m{f.swell_period_s ? ` · ${fmt(f.swell_period_s, 0)}s` : ''}</div>
                </div>
                <div>
                  <div className="opacity-70 text-[10px] uppercase">Wind</div>
                  <div className="font-bold text-base">{fmt(f.wind_speed_kmh, 0)} km/h</div>
                </div>
                <div>
                  <div className="opacity-70 text-[10px] uppercase">Gusts</div>
                  <div className="font-bold text-base">{fmt(f.wind_gust_kmh, 0)} km/h</div>
                </div>
              </div>
              {sev !== 'calm' && (
                <div className={`mt-2 text-[11px] ${palette.text} opacity-90`}>
                  {sev === 'rough'
                    ? 'Rough conditions — strongest divers are biased onto the first launch. Confirm with charter before departure.'
                    : 'Lumpy conditions — first launch favours more experienced divers.'}
                </div>
              )}
            </div>
          );
        })()}

        {plan.warnings?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3" data-testid="warnings-banner">
            <div className="flex items-center gap-2 text-amber-800 font-semibold mb-1"><AlertTriangle className="w-4 h-4" /> Warnings</div>
            <ul className="text-xs text-amber-800 list-disc pl-5 space-y-1">
              {plan.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </div>
        )}
        {saving && <div className="text-xs text-gray-400 mb-2">Saving…</div>}

        <DragDropContext onDragEnd={onDragEnd}>
          {boatGroups.map((bg, bi) => (
            <div key={`boat-${bi}`} className="mb-5" data-testid={`boat-group-${bi}`}>
              {boatGroups.length > 1 && (
                <div className="flex items-center gap-2 mb-2">
                  <Anchor className="w-5 h-5 text-[#FF6B35]" />
                  <h3 className="text-base font-bold text-[#0A2342]">{bg.boat.name || `Boat ${String.fromCharCode(65 + bi)}`}</h3>
                  <span className="text-xs text-gray-500">{bg.boat.launches_count} launch{bg.boat.launches_count === 1 ? '' : 'es'} × {bg.boat.capacity_per_launch} seats</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {bg.launches.map((L) => renderLaunchCard(L))}
              </div>
            </div>
          ))}
        </DragDropContext>

        <p className="text-xs text-gray-400 text-center mt-6">Drag to rearrange, or tap the edit icon on any diver / crew to move, swap or remove them. Use “+ Add” to assign divers or crew to a launch.</p>
      </div>

      {/* Diver action modal */}
      <Dialog open={!!diverMenu} onOpenChange={(o) => !o && setDiverMenu(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {diverMenu?.addToLaunch != null
                ? `Add diver to Launch ${diverMenu.addToLaunch + 1}`
                : (diverMenu && ((diverMap[diverMenu.diverId] || crewMap[diverMenu.diverId])?.name)) + ` — Launch ${diverMenu ? diverMenu.fromLaunch + 1 : ''}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {/* ----- MOVE / SWAP / REMOVE existing diver ----- */}
            {diverMenu?.diverId && (
              <>
                <div className="text-xs text-gray-500 mb-1">Move to another launch:</div>
                {plan && plan.launches.filter((L) => L.index !== diverMenu?.fromLaunch).map((L) => (
                  <Button
                    key={L.index}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => moveDiverToLaunch(L.index)}
                    disabled={diverMenu && L.divers.includes(diverMenu.diverId)}
                    data-testid={`diver-move-to-${L.index}`}
                  >
                    <Anchor className="w-4 h-4 mr-2" /> Launch {L.index + 1}
                    {diverMenu && L.divers.includes(diverMenu.diverId) && <span className="ml-auto text-xs text-gray-400">already here</span>}
                  </Button>
                ))}

                {/* Swap section */}
                <div className="border-t pt-2 mt-2">
                  <div className="text-xs text-gray-500 mb-1">Swap with another diver:</div>
                  {plan && plan.launches.flatMap((L) =>
                    L.divers
                      .filter((otherId) => otherId !== diverMenu.diverId)
                      .map((otherId) => {
                        const other = diverMap[otherId] || crewMap[otherId];
                        if (!other) return null;
                        return (
                          <Button
                            key={`swap-${L.index}-${otherId}`}
                            variant="outline"
                            className="w-full justify-start mb-1"
                            onClick={() => swapDiverWith(L.index, otherId)}
                            data-testid={`diver-swap-with-${otherId}`}
                          >
                            <span className="truncate">{other.name}</span>
                            <span className="ml-auto text-xs text-gray-400">L{L.index + 1}</span>
                          </Button>
                        );
                      })
                  )}
                </div>

                <div className="border-t pt-2 mt-2 space-y-2">
                  <Button variant="outline" className="w-full text-amber-700" onClick={removeDiverFromLaunch} data-testid="diver-remove-launch">
                    <X className="w-4 h-4 mr-2" /> Remove from this launch
                  </Button>
                  <Button variant="outline" className="w-full text-red-600" onClick={removeDiverFromDay} data-testid="diver-remove-day">
                    <X className="w-4 h-4 mr-2" /> Remove from entire day
                  </Button>
                </div>
              </>
            )}

            {/* ----- ADD a diver to this launch (from the trip roster) ----- */}
            {diverMenu?.addToLaunch != null && (
              <>
                <div className="text-xs text-gray-500 mb-1">Pick someone to add:</div>
                {(() => {
                  const usedThisLaunch = new Set(plan.launches[diverMenu.addToLaunch].divers);
                  const roster = [
                    ...(trip.divers || []).filter((d) => !d.skip_dive).map((d) => ({ ...d, kind: 'diver' })),
                    ...(trip.crew || []).map((c) => ({ ...c, kind: 'crew' })),
                  ];
                  if (roster.length === 0) {
                    return <div className="text-xs text-gray-400 py-4 text-center">No divers or crew on this trip yet.</div>;
                  }
                  return roster.map((p) => {
                    const here = usedThisLaunch.has(p.id);
                    const onOtherIdx = plan.launches.findIndex((L) => L.divers.includes(p.id) && L.index !== diverMenu.addToLaunch);
                    return (
                      <Button
                        key={p.id}
                        variant="outline"
                        className="w-full justify-start mb-1"
                        onClick={() => addDiverToLaunch(p.id)}
                        disabled={here}
                        data-testid={`diver-add-pick-${p.id}`}
                      >
                        <span className="truncate">{p.name}</span>
                        {p.kind === 'crew' && <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded capitalize">{p.role || 'staff'}</span>}
                        {here && <span className="ml-auto text-xs text-gray-400">already here</span>}
                        {!here && onOtherIdx >= 0 && <span className="ml-auto text-xs text-amber-600">also on L{onOtherIdx + 1}</span>}
                      </Button>
                    );
                  });
                })()}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Crew action modal */}
      <Dialog open={!!crewMenu} onOpenChange={(o) => !o && setCrewMenu(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {crewMenu?.crewId && `${crewMap[crewMenu.crewId]?.name} — Launch ${crewMenu.fromLaunch + 1}`}
              {crewMenu?.addToLaunch != null && `Add crew to Launch ${crewMenu.addToLaunch + 1}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {/* MOVE existing crew member */}
            {crewMenu?.crewId && (
              <>
                <div className="text-xs text-gray-500 mb-1">Move to another launch:</div>
                {plan.launches.filter((L) => L.index !== crewMenu.fromLaunch).map((L) => (
                  <Button
                    key={L.index}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => moveCrewToLaunch(L.index)}
                    disabled={L.crew.includes(crewMenu.crewId)}
                    data-testid={`crew-move-to-${L.index}`}
                  >
                    <Anchor className="w-4 h-4 mr-2" /> Launch {L.index + 1}
                    {L.crew.includes(crewMenu.crewId) && <span className="ml-auto text-xs text-gray-400">already here</span>}
                  </Button>
                ))}
                <div className="border-t pt-2 mt-2">
                  <Button variant="outline" className="w-full text-red-600" onClick={removeCrewFromLaunch} data-testid="crew-remove-launch">
                    <X className="w-4 h-4 mr-2" /> Remove from this launch
                  </Button>
                </div>
              </>
            )}
            {/* ADD crew to launch — pick from trip's full crew roster */}
            {crewMenu?.addToLaunch != null && (
              <>
                <div className="text-xs text-gray-500 mb-1">Pick crew to add:</div>
                {(trip.crew || []).length === 0 && (
                  <div className="text-xs text-gray-400 py-4 text-center">
                    No crew on this trip. Add crew from the trip detail page first.
                  </div>
                )}
                {(trip.crew || []).map((c) => {
                  const onThisLaunch = plan.launches[crewMenu.addToLaunch].crew.includes(c.id);
                  const onOtherIdx = plan.launches.findIndex((L, i) => i !== crewMenu.addToLaunch && L.crew.includes(c.id));
                  return (
                    <Button
                      key={c.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => addCrewToLaunch(c.id)}
                      disabled={onThisLaunch}
                      data-testid={`crew-add-pick-${c.id}`}
                    >
                      <span className={`w-2 h-2 rounded-full mr-2 ${ROLE_COLOR[c.role] || 'bg-gray-400'}`}></span>
                      <span className="truncate">{c.name}</span>
                      <span className="text-xs text-gray-400 capitalize ml-2">{c.role}</span>
                      {onThisLaunch && <span className="ml-auto text-xs text-gray-400">already here</span>}
                      {!onThisLaunch && onOtherIdx >= 0 && <span className="ml-auto text-xs text-amber-600">also on L{onOtherIdx + 1}</span>}
                    </Button>
                  );
                })}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit launch times modal */}
      <Dialog open={editTimes} onOpenChange={setEditTimes}>
        <DialogContent>
          <DialogHeader><DialogTitle>Launch Times</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500 mb-2">Kit-up time is auto-calculated as 1 hour before launch.</p>
          <div className="space-y-2">
            {draftTimes.map((t, i) => (
              <div key={`et-${i}`} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-20">Launch {i + 1}</span>
                <Input type="time" value={t} onChange={(e) => {
                  const next = [...draftTimes]; next[i] = e.target.value; setDraftTimes(next);
                }} className="flex-1" data-testid={`edit-launch-time-${i}`} />
                <span className="text-xs text-gray-500 w-28">Kit-up: <strong>{subKitUp(t) || '—'}</strong></span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTimes(false)}>Cancel</Button>
            <Button onClick={saveTimes} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="save-launch-times">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instructor dive log modal */}
      <Dialog open={logModal != null} onOpenChange={(o) => !o && setLogModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2"><BookOpenCheck className="w-5 h-5 text-[#FF6B35]" /> Instructor&apos;s Dive Log</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 italic">
            Saved logs appear immediately on this launch and on the diver share view, so divers without computers can copy the details into their own log books.
          </p>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto">
            <Input
              placeholder="Your name (e.g. DM Lee)"
              value={logForm.recorded_by_name}
              onChange={(e) => setLogForm({ ...logForm, recorded_by_name: e.target.value })}
              data-testid="log-recorder-input"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Date</label>
                <Input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} data-testid="log-date-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Time</label>
                <Input type="time" value={logForm.time} onChange={(e) => setLogForm({ ...logForm, time: e.target.value })} data-testid="log-time-input" />
              </div>
            </div>
            <Input
              placeholder="Dive site (e.g. Aliwal Shoal — Cathedral)"
              value={logForm.site}
              onChange={(e) => setLogForm({ ...logForm, site: e.target.value })}
              data-testid="log-site-input"
            />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Depth (m)</label>
                <Input type="number" step="0.1" value={logForm.depth_m} onChange={(e) => setLogForm({ ...logForm, depth_m: e.target.value })} data-testid="log-depth-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Water °C</label>
                <Input type="number" step="0.1" value={logForm.water_temp_c} onChange={(e) => setLogForm({ ...logForm, water_temp_c: e.target.value })} data-testid="log-temp-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Duration (min)</label>
                <Input type="number" value={logForm.duration_min} onChange={(e) => setLogForm({ ...logForm, duration_min: e.target.value })} data-testid="log-duration-input" />
              </div>
            </div>
            <Input
              placeholder="Notes (visibility, conditions, animals seen…)"
              value={logForm.notes}
              onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
              data-testid="log-notes-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {logModal != null && plan.launches.find((L) => L.index === logModal)?.log && (
              <Button variant="outline" className="text-red-600" onClick={clearLog} disabled={savingLog} data-testid="log-clear-btn">
                Remove log
              </Button>
            )}
            <Button variant="outline" onClick={() => setLogModal(null)}>Cancel</Button>
            <Button onClick={saveLog} disabled={savingLog} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="log-save-btn">
              {savingLog ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap launches dialog */}
      <Dialog open={swapModal !== null} onOpenChange={(o) => !o && setSwapModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Swap Launch {swapModal !== null ? swapModal + 1 : ''} with…</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">Picks up the entire launch — divers, crew, time and dive log — and swaps it with the one you choose.</p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {plan && plan.launches.filter((L) => L.index !== swapModal).map((L) => (
              <Button
                key={L.index}
                variant="outline"
                className="w-full justify-start"
                onClick={() => swapWith(L.index)}
                data-testid={`swap-with-${L.index}`}
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Launch {L.index + 1}
                <span className="ml-auto text-xs text-gray-500">{plan.launch_times?.[L.index] || '—'} · {L.divers.length} divers</span>
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapModal(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roll Call dialog — per-day attendance */}
      <Dialog open={rollCallOpen} onOpenChange={setRollCallOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><span className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#FF6B35]" /> Roll Call — {planDate}</span></DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">Mark each diver&apos;s intent for today. Marking someone <strong>Not diving</strong> excludes them from the auto-generator on the next Regenerate.</p>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {(trip.divers || []).map((d) => {
              const explicit = plan.attendance?.[d.id];
              const inferred = explicit || (d.skip_dive ? 'not_diving' : (plan.launches.some((L) => L.divers.includes(d.id)) ? 'diving' : 'undecided'));
              const opts = [
                { v: 'diving', label: 'Diving', color: 'bg-green-600 text-white border-green-600' },
                { v: 'not_diving', label: 'Not diving', color: 'bg-red-600 text-white border-red-600' },
                { v: 'undecided', label: 'Undecided', color: 'bg-amber-500 text-white border-amber-500' },
              ];
              return (
                <div key={d.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#0A2342] truncate">{d.name}</div>
                    <div className="text-xs text-gray-500">{d.certification_level || '—'}{d.skip_dive ? ' · globally skipping' : ''}</div>
                  </div>
                  <div className="flex gap-1">
                    {opts.map((o) => (
                      <button
                        key={o.v}
                        onClick={() => setDiverAttendance(d.id, o.v)}
                        className={`text-[11px] px-2 py-1 rounded border ${inferred === o.v ? o.color : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        data-testid={`rollcall-${d.id}-${o.v}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollCallOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dive report dialog */}
      <Dialog open={emailModal} onOpenChange={setEmailModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><span className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#FF6B35]" /> Email dive report</span></DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">Sends the day&apos;s dive report as PDF + CSV attachments. Recipients are remembered for next time.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Recipients (comma-separated emails)</label>
              <textarea
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                placeholder="ops@justdive.co.za, dive-team@justdive.co.za"
                rows={2}
                className="w-full text-sm border rounded p-2 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]"
                data-testid="email-report-recipients"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Note (optional)</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Wind picked up after Launch 2 — only 3 dived in the afternoon. See attached."
                rows={3}
                className="w-full text-sm border rounded p-2 focus:outline-none focus:ring-1 focus:ring-[#FF6B35]"
                data-testid="email-report-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModal(false)} disabled={emailSending}>Cancel</Button>
            <Button onClick={sendDiveReportEmail} disabled={emailSending} className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90" data-testid="email-report-send-btn">
              {emailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportPlanDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        api={api}
        tripId={tripId}
        planDate={planDate}
        plan={plan}
        onApplied={async (res) => {
          setImportOpen(false);
          setPlan(res.plan);
          toast.success(`Imported · ${res.launches_changed} launch${res.launches_changed === 1 ? '' : 'es'} updated`);
        }}
      />

      <PwaInstallBanner />
    </div>
  );
};

export default DayPlanner;
